// Навигатор страниц: миниатюры листов (схема строк страницы из разметки) с номерами; щелчок - перейти к странице.
import { useEffect, useRef } from 'react';
import type { Ctx } from '../../core/registry';
import { useApp } from '../../core/store';
import { usePages, pagesStore } from './store';
import { STRIDE } from './geometry';
import { zadanieTitle } from '../sto_intel/data';

/** Прокрутить холст к странице n (номер в документе, с учётом листов перед текстом). */
export function gotoPage(ctx: Ctx, n: number) {
  const s = pagesStore.getState();
  const canvas = document.querySelector('.canvas') as HTMLElement | null;
  if (!canvas) return;
  if (n <= s.frontCount) {
    let k = 0;
    for (const f of s.front) {
      if (n <= k + f.pages) {
        const el = document.querySelector(`[data-sheet="${f.id}"]`) as HTMLElement | null;
        if (el) canvas.scrollTop += el.getBoundingClientRect().top - canvas.getBoundingClientRect().top - 16;
        return;
      }
      k += f.pages;
    }
    return;
  }
  const idx = n - s.frontCount - 1;
  const main = document.querySelector('.sheet-main') as HTMLElement | null;
  if (!main || idx < 0 || idx >= s.main.length) return;
  const r = main.getBoundingClientRect();
  const k = r.width / (main.offsetWidth || 1) || 1;
  canvas.scrollTop += r.top + idx * STRIDE * k - canvas.getBoundingClientRect().top - 16;
  const ed = ctx.editor;
  const pos = s.main[idx].pos;
  if (ed && !ed.isDestroyed && pos >= 0) {
    try {
      ed.commands.setTextSelection(Math.min(pos + 1, ed.state.doc.content.size));
      ed.view.focus();
      requestAnimationFrame(() => { canvas.scrollTop += 0; });
    } catch { /* позиция вне текста */ }
  }
}

export function PageNavigator({ ctx }: { ctx: Ctx }) {
  const main = usePages((s) => s.main);
  const front = usePages((s) => s.front);
  const frontCount = usePages((s) => s.frontCount);
  const cur = usePages((s) => s.current);
  const ready = usePages((s) => s.ready);
  const meta = useApp((s) => s.meta);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = list.current?.querySelector('.pg-nav-item.is-current') as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [cur]);
  if (!ready) return <div className="pg-nav-empty muted">Включите «Вид → Разметка страницы».</div>;
  const names: Record<string, string> = { title: 'Титульный лист', zadanie: zadanieTitle(meta), remarks: 'Лист для замечаний',
    referat: meta?.annotation?.enabled ? 'Аннотация' : 'Реферат', toc: 'Оглавление' };
  const items: { n: number; label: string; snippet: string; front: boolean }[] = [];
  let k = 0;
  for (const f of front) for (let i = 0; i < f.pages; i++) items.push({ n: ++k, label: names[f.id] || f.id, snippet: '', front: true });
  main.forEach((p, i) => items.push({ n: frontCount + i + 1, label: '', snippet: p.snippet, front: false }));
  return (
    <div className="pg-nav" ref={list} data-testid="page-nav">
      {items.map((it) => (
        <button key={it.n} type="button" className={'pg-nav-item' + (it.n === cur ? ' is-current' : '') + (it.front ? ' is-front' : '')}
          onClick={() => gotoPage(ctx, it.n)} data-testid="page-nav-item">
          <span className="pg-nav-sheet">
            {it.front ? <span className="pg-nav-front">{it.label}</span> : <span className="pg-nav-lines">{it.snippet}</span>}
          </span>
          <span className="pg-nav-n">{it.n}</span>
        </button>
      ))}
    </div>
  );
}
